// ============================================================================
// RECOVERY OFFICER DASHBOARD HOOKS — Phase 8.D
// ============================================================================
// One hook per widget. `refreshKey` lets the parent screen force a refetch
// across all widgets simultaneously (the toolbar Refresh button).

import { useState, useEffect, useCallback } from 'react';
import { recoveryDashboardService } from '../../api/services/recoveryDashboardService';
import type {
  ActionTodayItem,
  RecentClosureItem,
  UnclaimedQueueItem,
  WorkloadSummary,
} from '../../api/types/recoveryDashboard.types';

export const useMyWorkload = (refreshKey?: number) => {
  const [data, setData] = useState<WorkloadSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await recoveryDashboardService.getMyWorkload());
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetch(); }, [fetch, refreshKey]);
  return { data, loading, error, refetch: fetch };
};

export const useUnclaimedQueue = (limit?: number, refreshKey?: number) => {
  const [data, setData] = useState<UnclaimedQueueItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await recoveryDashboardService.getUnclaimedQueue(limit));
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [limit]);

  useEffect(() => { fetch(); }, [fetch, refreshKey]);
  return { data, loading, error, refetch: fetch };
};

export const useActionToday = (limit?: number, refreshKey?: number) => {
  const [data, setData] = useState<ActionTodayItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await recoveryDashboardService.getActionToday(limit));
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [limit]);

  useEffect(() => { fetch(); }, [fetch, refreshKey]);
  return { data, loading, error, refetch: fetch };
};

export const useRecentClosures = (
  days?: number,
  limit?: number,
  refreshKey?: number,
) => {
  const [data, setData] = useState<RecentClosureItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await recoveryDashboardService.getRecentClosures(days, limit));
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [days, limit]);

  useEffect(() => { fetch(); }, [fetch, refreshKey]);
  return { data, loading, error, refetch: fetch };
};
